import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "@phosphor-icons/react";
import { SectionHead, Reveal } from "@/components/Reveal";
import { DemoChat } from "@/components/DemoChat";
import { AGENTS } from "@/data/content";

export default function DigitalTeam() {
    const [active, setActive] = useState(null);

    return (
        <section id="team" className="relative border-t border-white/10 py-28 md:py-36" data-testid="team-section">
            <div className="mx-auto max-w-[1400px] px-5 md:px-10">
                <SectionHead
                    number="03"
                    label="Meet your digital team"
                    title="AI employees. No robots. Just work getting done."
                    copy="Each AI employee is a professional business interface trained on your company — answering, qualifying, booking and following up while you work. Try a live simulation."
                />
                <div className="mt-16 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    {AGENTS.map((agent, i) => (
                        <Reveal key={agent.id} delay={(i % 4) * 0.08} className={i === 0 || i === 9 ? "sm:col-span-2" : ""}>
                            <motion.div
                                whileHover={{ y: -4 }}
                                transition={{ duration: 0.3 }}
                                className="group flex h-full flex-col border border-white/12 bg-surface p-7 transition-colors duration-300 hover:border-white/35"
                                data-testid={`agent-card-${agent.id}`}
                            >
                                <agent.icon size={26} weight="light" className="text-white/80" />
                                <h3 className="mt-6 font-display text-lg font-bold tracking-tight text-white">{agent.name}</h3>
                                <p className="mt-2 flex-1 text-sm font-light leading-relaxed text-white/50">{agent.tag}</p>
                                <button
                                    onClick={() => setActive(agent)}
                                    data-testid={`agent-try-${agent.id}`}
                                    className="mt-6 w-full border border-white/25 py-3 text-[11px] font-bold uppercase tracking-[0.22em] text-white transition-colors duration-300 group-hover:bg-white group-hover:text-black"
                                >
                                    Try demo
                                </button>
                            </motion.div>
                        </Reveal>
                    ))}
                </div>
                <Reveal delay={0.15}>
                    <p className="mt-8 text-xs font-light text-white/35">
                        All demonstrations are simulations showing how each AI employee works — no real customer data involved.
                    </p>
                </Reveal>
            </div>

            <AnimatePresence>
                {active && (
                    <motion.div
                        className="fixed inset-0 z-[110] flex items-end justify-center bg-black/80 backdrop-blur-sm sm:items-center sm:p-6"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setActive(null)}
                        data-testid="agent-modal"
                    >
                        <motion.div
                            initial={{ opacity: 0, y: 40, scale: 0.98 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 30, scale: 0.98 }}
                            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                            className="relative flex h-[85vh] w-full max-w-lg flex-col border border-white/15 bg-surface sm:h-[620px]"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <button
                                onClick={() => setActive(null)}
                                data-testid="agent-modal-close"
                                aria-label="Close demo"
                                className="absolute -top-12 right-0 flex h-10 w-10 items-center justify-center border border-white/25 text-white transition-colors hover:bg-white hover:text-black"
                            >
                                <X size={18} />
                            </button>
                            <DemoChat
                                title={active.name}
                                subtitle="AI employee · HIGHTS digital team"
                                greeting={active.greeting}
                                suggestions={active.suggestions}
                                handoff={active.handoff}
                                live
                                agentId={active.id}
                            />
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
}
